// src/pages/ActSearch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import Navbar from '../components/Navbar';
import ApiService from '../services/apiService';
import LeApiService from '../services/leApiService';

const ActSearch = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([{ act: '', section: '' }]);
  const [actsList, setActsList] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(-1);
  const [sectionsCache, setSectionsCache] = useState({});
  const [activeSectionDropdown, setActiveSectionDropdown] = useState(-1);
  const [sectionsLoading, setSectionsLoading] = useState({});
  const dropdownRefs = useRef([]);

  useEffect(() => {
    document.body.style.paddingTop = '0';
   // ApiService.logPageVisit('Act Wise Search');
    LeApiService.getActList().then(data => {
      if (Array.isArray(data)) setActsList(data);
    }).catch(() => {});
    return () => { document.body.style.paddingTop = ''; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inAnyRow = dropdownRefs.current.some(ref => ref && ref.contains(e.target));
      if (!inAnyRow) { setActiveDropdown(-1); setActiveSectionDropdown(-1); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown, activeSectionDropdown]);

  const handleRowChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
    if (field === 'act') {
      setActiveDropdown(value.length >= 2 ? index : -1);
      setActiveSectionDropdown(-1);
    }
    if (field === 'section') {
      setActiveDropdown(-1);
      const actName = rows[index].act;
      setActiveSectionDropdown(actName && sectionsCache[actName] ? index : -1);
    }
  };

  const selectAct = (index, actName) => {
    const updated = [...rows];
    updated[index].act = actName;
    updated[index].section = '';
    setRows(updated);
    setActiveDropdown(-1);
    fetchSections(actName);
  };

  const fetchSections = async (actName) => {
    if (sectionsCache[actName]) return sectionsCache[actName];
    setSectionsLoading(prev => ({ ...prev, [actName]: true }));
    try {
      const resp = await fetch(`https://api.ljsoftonline.com/Judgement/GetActSections/${encodeURIComponent(actName)}`);
      if (resp.ok) {
        const data = await resp.json();
        setSectionsCache(prev => ({ ...prev, [actName]: data }));
        return data;
      }
    } catch (e) { console.error('Failed to fetch sections:', e); }
    finally { setSectionsLoading(prev => ({ ...prev, [actName]: false })); }
    return [];
  };

  const getFilteredActs = (query) => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return actsList.filter(a => a.toLowerCase().includes(q)).slice(0, 15);
  };

  const getFilteredSections = (index) => {
    const actName = rows[index]?.act;
    const sectionQuery = rows[index]?.section || '';
    const sections = sectionsCache[actName] || [];
    if (!sectionQuery) return sections;
    const filtered = sections.filter(s => s.toLowerCase().includes(sectionQuery.toLowerCase()));
    return filtered.length > 0 ? filtered : sections;
  };

  const selectSection = (index, section) => {
    const updated = [...rows];
    updated[index].section = section;
    setRows(updated);
    setActiveSectionDropdown(-1);
  };

  const addRow = () => setRows([...rows, { act: '', section: '' }]);

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
    setActiveDropdown(-1);
  };

  const handleClear = () => {
    setRows([{ act: '', section: '' }]);
    setError('');
    setActiveDropdown(-1);
  };

  const hasValidRow = rows.some(r => r.act.trim() || r.section.trim());

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!hasValidRow || isLoading) return;
    setIsLoading(true);
    setError('');
    setActiveDropdown(-1);
    try {
      const requests = rows
        .filter(r => r.act.trim() || r.section.trim())
        .map(r => {
          let actSearch = r.act.trim();
          if (r.act.trim() && r.section.trim()) actSearch = `${r.act.trim()} -- ${r.section.trim()}`;
          else if (r.section.trim()) actSearch = r.section.trim();
          return { act: actSearch };
        });

      const payload = {
        requests, sortBy: 'rele', sortOrder: 'desc',
        page: 1, pageSize: 25, inst: '', prompt: 'Act Wise Search',
      };

//      ApiService.logSearch('ActWise', rows.map(r => `${r.act} - ${r.section}`).join(', '));
      const apiResponse = await ApiService.executeAllSearch(payload);
      const searchResults = apiResponse.hits || [];

      if (searchResults.length === 0) {
        setError('No results found. Try different act names or sections.');
        setIsLoading(false);
        return;
      }

      const resultsData = {
        results: apiResponse.hits || [],
        totalCount: apiResponse.total || 0,
        query: rows.map(r => [r.act, r.section].filter(Boolean).join(' - ')).join(', '),
        searchType: 'Act Wise Search',
        timestamp: new Date().toISOString(),
        courtsList: apiResponse.courtsList || [],
        yearList: apiResponse.yearList || [],
        searchData: {
          query: '', searchType: 'Act Wise Search',
          sortOrder: 'rele', searchIn: 'B', nearWordsDistance: '5'
        }
      };
      localStorage.setItem('searchResults', JSON.stringify(resultsData));
      navigate('/results');
    } catch (err) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="gojuris-layout">
     
      <div className="gojuris-main">
        <Navbar />
        <div className="ai-search-page">
          {error && (
            <div className="error-alert">
              <i className="bx bx-error-circle"></i>
              {error}
            </div>
          )}

          <div className="ai-search-container">
            <div className="search-header">
              <div className="search-badge">
                <i className="bx bx-book-open"></i>
                <span>Act Wise Search</span>
              </div>
            </div>

            <div className="search-hero">
              <h1 className="hero-title">
                Search judgments by Acts and Sections
              </h1>

              <form onSubmit={handleSearch}>
                <div className="act-rows-container">
                  {rows.map((row, index) => {
                    const filtered = activeDropdown === index ? getFilteredActs(row.act) : [];
                    return (
                      <div key={index} className="act-row" ref={el => dropdownRefs.current[index] = el}>
                        <div className="act-row-number">{index + 1}</div>
                        <div className="act-input-group">
                          <div className="act-autocomplete-wrapper">
                            <input
                              type="text"
                              className="act-input"
                              placeholder="Enter Act name (e.g., Indian Penal Code)"
                              value={row.act}
                              onChange={(e) => handleRowChange(index, 'act', e.target.value)}
                              onFocus={() => { if (row.act.length >= 2) setActiveDropdown(index); }}
                              disabled={isLoading}
                              autoComplete="off"
                            />
                            {filtered.length > 0 && (
                              <div className="act-dropdown">
                                {filtered.map((act, i) => (
                                  <div key={i} className="act-dropdown-item"
                                    onMouseDown={(e) => { e.preventDefault(); selectAct(index, act); }}>
                                    {act}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="section-autocomplete-wrapper">
                            <input
                              type="text"
                              className="act-input section-input"
                              placeholder="Section (e.g., 302)"
                              value={row.section}
                              onChange={(e) => handleRowChange(index, 'section', e.target.value)}
                              onFocus={() => {
                                setActiveDropdown(-1);
                                const actName = row.act.trim();
                                if (actName.length >= 3) {
                                  setActiveSectionDropdown(index);
                                  if (!sectionsCache[actName]?.length && !sectionsLoading[actName]) {
                                    fetchSections(actName);
                                  }
                                }
                              }}
                              disabled={isLoading}
                              autoComplete="off"
                            />
                            {activeSectionDropdown === index && (
                              <div className="act-dropdown section-dropdown">
                                {sectionsLoading[row.act?.trim()] ? (
                                  <div className="section-loading">
                                    <i className="bx bx-loader bx-spin"></i>
                                    <span>Loading sections...</span>
                                  </div>
                                ) : getFilteredSections(index).length > 0 ? (
                                  getFilteredSections(index).map((sec, i) => (
                                    <div key={i} className="act-dropdown-item"
                                      onMouseDown={(e) => { e.preventDefault(); selectSection(index, sec); }}>
                                      {sec}
                                    </div>
                                  ))
                                ) : (
                                  <div className="section-loading" style={{ color: '#999' }}>
                                    {row.act?.trim() ? 'No sections found' : 'Select an act first'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {rows.length > 1 && (
                          <button type="button" className="act-remove-btn"
                            onClick={() => removeRow(index)} title="Remove">
                            <i className="bx bx-x"></i>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="act-actions">
                  <button type="button" className="act-add-btn" onClick={addRow} disabled={isLoading}>
                    <i className="bx bx-plus"></i>
                    Add Act / Section
                  </button>
                </div>

                <div className="act-search-actions">
                  <button type="submit" className="act-search-btn" disabled={isLoading || !hasValidRow}>
                    {isLoading ? (
                      <><i className="bx bx-loader bx-spin"></i> Searching...</>
                    ) : (
                      <><i className="bx bx-search"></i> Search</>
                    )}
                  </button>
                  <button type="button" className="act-clear-btn" onClick={handleClear} disabled={isLoading}>
                    <i className="bx bx-refresh"></i> Clear
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .gojuris-layout { display: flex; min-height: 100vh; }

        .gojuris-main {
          flex: 1;
          margin-left: 70px;
          width: calc(100% - 70px);
          background: #f8f9fa;
          min-height: 100vh;
        }

        .ai-search-page {
          min-height: calc(100vh - 80px);
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .ai-search-container {
          max-width: 1200px;
          width: 100%;
          margin-top: -100px;
          text-align: center;
        }

        .error-alert {
          background: #f8d7da;
          color: #721c24;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
        }

        .search-header { margin-bottom: 3rem; }

        /* ✅ Badge - Orange theme */
        .search-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, rgba(245,166,35,0.08), rgba(233,30,99,0.08));
          border: 1px solid rgba(245, 166, 35, 0.3);
          border-radius: 25px;
          color: #E91E63;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.15);
        }
        .search-badge i { font-size: 16px; }

        .search-hero { margin-bottom: 4rem; }

        .hero-title {
          font-size: clamp(1.6rem, 4vw, 2rem);
          font-weight: 300;
          color: #333;
          line-height: 1.3;
          margin-bottom: 2rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        /* ✅ Act rows - Orange focus */
        .act-rows-container {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .act-row {
          display: flex;
          align-items: center;
          gap: 10px;
          background: white;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          padding: 10px 14px;
          transition: all 0.2s ease;
        }
        .act-row:focus-within {
          border-color: #E91E63;
          box-shadow: 0 2px 12px rgba(245, 166, 35, 0.2);
        }

        /* ✅ Row number - Orange gradient */
        .act-row-number {
          width: 28px; height: 28px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 600; flex-shrink: 0;
        }

        .act-input-group { flex: 1; display: flex; gap: 10px; }

        .act-autocomplete-wrapper { flex: 2; position: relative; }

        .act-input {
          width: 100%; border: none; background: transparent;
          padding: 0.75rem 1rem; font-size: 15px; outline: none; color: #333;
        }
        .act-input::placeholder { color: #aaa; }

        .section-autocomplete-wrapper {
          flex: 1; position: relative;
          border-left: 1px solid #e9ecef;
        }
        .section-input { width: 100%; padding-left: 1rem; }
        .section-dropdown { min-width: 180px; }

        /* ✅ Section loading - Orange */
        .section-loading {
          padding: 14px; text-align: center;
          color: #E91E63; font-size: 14px;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .section-loading i { font-size: 18px; }

        .act-dropdown {
          position: absolute; top: 100%; left: 0; right: 0;
          background: white; border: 1px solid #e9ecef; border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          max-height: 250px; overflow-y: auto; z-index: 1000;
          margin-top: 4px; text-align: left;
        }
        .act-dropdown-item {
          padding: 10px 14px; font-size: 14px; color: #333;
          cursor: pointer; border-bottom: 1px solid #f0f0f0;
          transition: all 0.15s;
        }
        .act-dropdown-item:last-child { border-bottom: none; }

        /* ✅ Dropdown hover - Orange */
        .act-dropdown-item:hover {
          background: rgba(245, 166, 35, 0.08);
          color: #c41849;
        }

        .act-remove-btn {
          width: 32px; height: 32px; border: none;
          background: #fee2e2; color: #dc3545; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 18px; flex-shrink: 0; transition: all 0.2s;
        }
        .act-remove-btn:hover { background: #dc3545; color: white; }

        .act-actions {
          max-width: 900px; margin: 16px auto 0; text-align: left;
        }

        /* ✅ Add button - Orange dashed */
        .act-add-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          border: 2px dashed #E91E63;
          background: transparent; color: #E91E63;
          border-radius: 8px; font-size: 14px; font-weight: 500;
          cursor: pointer; transition: all 0.2s;
        }
        .act-add-btn:hover:not(:disabled) {
          background: rgba(245, 166, 35, 0.08);
        }
        .act-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .act-search-actions {
          max-width: 900px; margin: 24px auto 0;
          display: flex; gap: 12px; justify-content: center;
        }

        /* ✅ Search button - Orange gradient */
        .act-search-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 36px;
          background: linear-gradient(135deg, #E91E63 0%, #c41849 100%);
          color: white; border: none; border-radius: 50px;
          font-size: 16px; font-weight: 600; cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.3);
        }
        .act-search-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #E09000 0%, #C2185B 100%);
          box-shadow: 0 6px 20px rgba(245, 166, 35, 0.4);
          transform: translateY(-1px);
        }
        .act-search-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .act-clear-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 24px; background: white; color: #6c757d;
          border: 1px solid #dee2e6; border-radius: 50px;
          font-size: 15px; cursor: pointer; transition: all 0.2s;
        }
        .act-clear-btn:hover:not(:disabled) { background: #f8f9fa; color: #333; }

        @media (max-width: 768px) {
          .act-input-group { flex-direction: column; gap: 0; }
          .section-input { border-left: none; border-top: 1px solid #e9ecef; padding-left: 1rem; }
          .act-row { flex-wrap: wrap; }
          .act-search-actions { flex-direction: column; align-items: stretch; }
        }

        @media (max-width: 575.98px) {
          .gojuris-main { margin-left: 60px; width: calc(100% - 60px); }
        }
      `}</style>
    </div>
  );
};

export default ActSearch;